// Fetchers shared by the dashboard page and its sections. The page starts
// every dashboard request at once (see app/[tenant]/dashboard/page.tsx);
// the sections then read the same cache entries, so their queryFn and the
// page's prefetch must be the same function.
import apiClient from '@/lib/api';
import {
  DashboardSnapshotSchema,
  PulseDigestSchema,
  type DashboardSnapshot,
  type PulseDigest,
} from '@/lib/schemas/dashboard';
import type { IndustryAnalysis } from '@/types/dashboard';

type Issue = { path: PropertyKey[]; message: string };

// Runtime contract validation (plan item #23). If the API drifts, we throw
// here with a clear "contract violation" message instead of failing several
// components deeper with a TypeError.
function contractViolation(what: string, issues: Issue[]): Error {
  if (process.env.NODE_ENV !== 'production') {
    console.error(`[api] ${what} contract violation`, issues);
  }
  return new Error(
    `${what.charAt(0).toUpperCase() + what.slice(1)} contract violation: ${issues
      .map((i) => `${i.path.join('.') || '<root>'}: ${i.message}`)
      .join('; ')}`,
  );
}

export async function fetchSnapshot(filters: Record<string, unknown>): Promise<DashboardSnapshot> {
  const { data } = await apiClient.post('/api/v1/dashboard/snapshot', filters);
  const parsed = DashboardSnapshotSchema.safeParse(data);
  if (!parsed.success) throw contractViolation('dashboard snapshot', parsed.error.issues);
  return parsed.data;
}

export async function fetchPulse(fiscalYear: number, search: string): Promise<PulseDigest> {
  const { data } = await apiClient.get('/api/v1/dashboard/pulse', {
    params: { fiscal_year: fiscalYear, ...(search && { search }) },
  });
  const parsed = PulseDigestSchema.safeParse(data);
  if (!parsed.success) throw contractViolation('pulse digest', parsed.error.issues);
  return parsed.data;
}

export async function fetchBudgetDeltaIndustry(
  filtersWithFY: Record<string, unknown>,
): Promise<IndustryAnalysis> {
  const { data } = await apiClient.post('/api/v1/dashboard/budget-delta-industry', filtersWithFY);
  return data;
}

// placeholderData for filter-keyed queries: while a new filter combination
// loads, keep the previous figures on screen (flagged as updating) instead
// of dropping back to skeletons and remounting every chart. Only within the
// same tenant — another tenant's numbers must never stand in, even briefly.
export function keepPreviousForTenant<T>(tenant: string) {
  return (prev: T | undefined, prevQuery?: { queryKey: readonly unknown[] }) =>
    prevQuery?.queryKey.includes(tenant) ? prev : undefined;
}
